import React, { useEffect } from 'react';
import { useQuery } from '@apollo/client';
import moment from 'moment/moment';
import { useRouter } from 'next/router';
import Link from 'nextDir/modules/Router/Link';
import { defineMessages, useIntl } from 'react-intl';

import { useCountryData } from 'src/common/hooks/useCountryData';
import { usePageNumberTranslation } from 'src/common/hooks/usePageNumberTranslation';
import { ROUTES } from 'src/common/routes';
import GlintsContainer from 'src/components/GlintsContainer';
import { MetadataRenderer } from 'src/components/MetaDescription/MetadataRenderer';
import { AnchorPagination } from 'src/components/Pagination/AnchorPagination';
import {
  GetActiveJobsArg,
  GetCustomSlugPagesWithActiveJobsData,
  queryCustomSlugPagesWithActiveJobs,
} from 'src/modules/Seo/graphql';
import * as S from 'src/modules/Seo/JobsIndexPage.sc';

const getTime = () => moment().format('MMMM YYYY');
const getMonthNumber = () => moment().format('M');
const getMonth = () => moment().format('MMMM');
const getYear = () => moment().format('YYYY');

export const JobsByCustomSlugPages: React.FC<
  React.PropsWithChildren<{
    initialFindJobsData: GetCustomSlugPagesWithActiveJobsData;
  }>
> = ({ initialFindJobsData }) => {
  const router = useRouter();
  const intl = useIntl();
  const { countryCode, englishCountryName } = useCountryData();

  const currentPage = Number(router.query.page) || 1;
  const pageTranslation = usePageNumberTranslation(currentPage);

  const shouldSkipQuery =
    initialFindJobsData?.getCustomSlugPagesWithActiveJobs?.customSlugPages
      ?.length > 0;

  const { data } = useQuery<
    GetCustomSlugPagesWithActiveJobsData,
    GetActiveJobsArg
  >(queryCustomSlugPagesWithActiveJobs, {
    variables: {
      countryCode,
      pageSize: 60,
      currentPage,
    },
    skip: shouldSkipQuery,
  });

  const browseFindJobsData = shouldSkipQuery ? initialFindJobsData : data;

  useEffect(function redirectIfNeeded() {
    const pageOutOfRange =
      browseFindJobsData &&
      currentPage >
        browseFindJobsData.getCustomSlugPagesWithActiveJobs.pageInfo.totalPage;

    const positiveTotalCount =
      browseFindJobsData?.getCustomSlugPagesWithActiveJobs.pageInfo.totalCount >
      0;

    if (positiveTotalCount && pageOutOfRange) {
      router.push({ pathname: router.pathname }, undefined, {
        shallow: true,
      });
    }
  });

  const time = getTime();
  const monthNumber = getMonthNumber();
  const month = getMonth();
  const year = getYear();

  return (
    <>
      <MetadataRenderer
        title={intl.formatMessage(messages.title, {
          country: englishCountryName,
          pageTranslation,
          time,
          monthNumber,
          month,
          year,
        })}
        description={intl.formatMessage(messages.description, {
          country: englishCountryName,
          time,
          monthNumber,
          month,
          year,
        })}
        glintsTrackingName="explore"
      />
      <GlintsContainer withMarginBottom={true}>
        <S.Header>
          {intl.formatMessage(messages.heading, {
            country: englishCountryName,
          })}
        </S.Header>
        {browseFindJobsData && (
          <>
            <S.ListContainer>
              <S.LinkList>
                {browseFindJobsData.getCustomSlugPagesWithActiveJobs.customSlugPages.map(
                  ({ customSlugPage }) => {
                    return (
                      <S.LinkItem key={customSlugPage.name}>
                        <Link
                          href={`/${ROUTES.opportunitiesJobsExplore}`}
                          as={`/find-jobs/${customSlugPage.slug}`}
                        >
                          {intl.formatMessage(messages.anchor, {
                            csp: customSlugPage.name,
                          })}
                        </Link>
                      </S.LinkItem>
                    );
                  }
                )}
              </S.LinkList>
            </S.ListContainer>
            {browseFindJobsData.getCustomSlugPagesWithActiveJobs.pageInfo
              .totalPage > 1 && (
              <S.PaginationContainer>
                <AnchorPagination
                  totalPage={
                    browseFindJobsData.getCustomSlugPagesWithActiveJobs.pageInfo
                      .totalPage
                  }
                  currentPage={currentPage}
                  basePathname="/browse/find-jobs"
                />
              </S.PaginationContainer>
            )}
          </>
        )}
      </GlintsContainer>
    </>
  );
};

const messages = defineMessages({
  title: {
    id: 'jobs-by_custom-slug-pages_meta_title',
    defaultMessage:
      'Popular Jobs & Vacancies in {country} - {month} {year} {pageTranslation}| Glints',
  },
  description: {
    id: 'jobs-by_custom-slug-pages_meta_description',
    defaultMessage:
      'Browse popular jobs in {country}. Discover newest and interesting jobs opportunities across Asia Pacific with Glints!',
  },
  heading: {
    id: 'jobs-by_custom-slug-pages_meta_heading',
    defaultMessage: 'Popular Jobs in {country} You Might be Interested in',
  },
  anchor: {
    id: 'jobs-by_custom-slug-pages_meta_anchor',
    defaultMessage: '{csp} Jobs',
  },
});
